import React, { useState, useEffect } from 'react';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { ChevronDown, ChevronRight, RotateCcw, ChevronsUpDown, Check } from 'lucide-react';
import languagesData from '../data/languages.json';

const RECENT_LANGUAGES_KEY = 'rag_recent_languages';
const MAX_RECENT = 5;

function getRecentLanguages() {
  try {
    const stored = localStorage.getItem(RECENT_LANGUAGES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function addRecentLanguage(code) {
  if (!code || code === 'auto') return;
  const recent = getRecentLanguages().filter(c => c !== code);
  recent.unshift(code);
  localStorage.setItem(RECENT_LANGUAGES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

/**
 * ScanOptionsPanel — Reusable component for Scan Options controls.
 *
 * Props:
 * - value: object (current scan options state)
 * - onChange: (newValue) => void
 * - onResetToDefaults: () => void (fetches MongoDB defaults and applies them)
 * - collapsible: boolean (default true — collapsed by default in dialogs)
 * - defaultExpanded: boolean (default false — for settings card, set to true)
 * - loading: boolean (show loading state while defaults are being fetched)
 * - systemDefaultLanguage: string (the system default language code, for badge display)
 */
export function ScanOptionsPanel({ value, onChange, onResetToDefaults, collapsible = true, defaultExpanded = false, loading = false, systemDefaultLanguage = 'auto' }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [langOpen, setLangOpen] = useState(false);

  if (!value) return null;

  const update = (field, val) => {
    onChange({ ...value, [field]: val });
  };

  const handleLanguageSelect = (code) => {
    update('language', code);
    addRecentLanguage(code);
    setLangOpen(false);
  };

  // Build language list: auto first, then recent, then all sorted by name
  const allLangs = languagesData.languages;
  const recentCodes = getRecentLanguages();
  const recentLangs = recentCodes
    .map(code => allLangs.find(l => l.code === code))
    .filter(Boolean);
  const currentLang = allLangs.find(l => l.code === (value.language || 'auto')) || allLangs[0];

  const content = (
    <div className="space-y-3">
      {/* Language Selection (Phase 6.5) */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">Document Language</Label>
          <p className="text-[10px] text-gray-500">Language hint for transcription and summaries</p>
        </div>
        <Popover open={langOpen} onOpenChange={setLangOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={langOpen}
              className="w-48 h-7 text-xs justify-between"
              data-testid="language-selector-trigger"
            >
              <span className="flex items-center gap-1 truncate">
                <span>{currentLang.flag}</span>
                <span>{currentLang.name}</span>
                <span className="text-gray-400">({currentLang.code})</span>
                {currentLang.code === systemDefaultLanguage && (
                  <span className="text-[9px] text-gray-400 bg-gray-100 rounded px-1">default</span>
                )}
              </span>
              <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="end">
            <Command>
              <CommandInput placeholder="Search languages..." className="text-xs" />
              <CommandList>
                <CommandEmpty>No language found.</CommandEmpty>
                {/* Auto-detect pinned at top */}
                <CommandGroup heading="Default">
                  <CommandItem
                    value="auto Auto-detect"
                    onSelect={() => handleLanguageSelect('auto')}
                    className="text-xs"
                    data-testid="language-option-auto"
                  >
                    <Check className={`mr-1 h-3 w-3 ${value.language === 'auto' || !value.language ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="mr-1">{allLangs[0].flag}</span>
                    Auto-detect
                    {systemDefaultLanguage === 'auto' && (
                      <span className="ml-auto text-[9px] text-gray-400 bg-gray-100 rounded px-1">default</span>
                    )}
                  </CommandItem>
                </CommandGroup>
                {/* Recently used */}
                {recentLangs.length > 0 && (
                  <CommandGroup heading="Recent">
                    {recentLangs.map(lang => (
                      <CommandItem
                        key={`recent-${lang.code}`}
                        value={`${lang.code} ${lang.name}`}
                        onSelect={() => handleLanguageSelect(lang.code)}
                        className="text-xs"
                      >
                        <Check className={`mr-1 h-3 w-3 ${value.language === lang.code ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="mr-1">{lang.flag}</span>
                        {lang.name}
                        <span className="ml-1 text-gray-400">({lang.code})</span>
                        {lang.code === systemDefaultLanguage && (
                          <span className="ml-auto text-[9px] text-gray-400 bg-gray-100 rounded px-1">default</span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {/* All languages sorted by name */}
                <CommandGroup heading="All Languages">
                  {allLangs
                    .filter(l => l.code !== 'auto')
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(lang => (
                      <CommandItem
                        key={lang.code}
                        value={`${lang.code} ${lang.name}`}
                        onSelect={() => handleLanguageSelect(lang.code)}
                        className="text-xs"
                        data-testid={`language-option-${lang.code}`}
                      >
                        <Check className={`mr-1 h-3 w-3 ${value.language === lang.code ? 'opacity-100' : 'opacity-0'}`} />
                        <span className="mr-1">{lang.flag}</span>
                        {lang.name}
                        <span className="ml-1 text-gray-400">({lang.code})</span>
                        {lang.code === systemDefaultLanguage && (
                          <span className="ml-auto text-[9px] text-gray-400 bg-gray-100 rounded px-1">default</span>
                        )}
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Images */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">Extract Images</Label>
          <p className="text-[10px] text-gray-500">OCR and describe images found in documents</p>
        </div>
        <Switch checked={value.extract_images} onCheckedChange={(v) => update('extract_images', v)} />
      </div>
      {value.extract_images && (
        <div className="flex items-center gap-2 pl-4 text-xs">
          <span className="text-gray-500">Min size:</span>
          <Input type="number" min={1} value={value.min_image_width} onChange={(e) => update('min_image_width', parseInt(e.target.value) || 1)} className="w-16 h-6 text-xs" />
          <span className="text-gray-500">&times;</span>
          <Input type="number" min={1} value={value.min_image_height} onChange={(e) => update('min_image_height', parseInt(e.target.value) || 1)} className="w-16 h-6 text-xs" />
          <span className="text-gray-500">px</span>
        </div>
      )}

      {/* Videos */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">Extract Videos</Label>
          <p className="text-[10px] text-gray-500">Transcribe embedded YouTube, Vimeo, HTML5 videos</p>
        </div>
        <Switch checked={value.extract_videos} onCheckedChange={(v) => update('extract_videos', v)} />
      </div>

      {/* Audio */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">Extract Audio</Label>
          <p className="text-[10px] text-gray-500">Transcribe embedded audio files</p>
        </div>
        <Switch checked={value.extract_audio} onCheckedChange={(v) => update('extract_audio', v)} />
      </div>

      {/* Linked PDFs */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">Follow Linked PDFs</Label>
          <p className="text-[10px] text-gray-500">Process PDF files linked within documents</p>
        </div>
        <Switch checked={value.extract_linked_pdfs} onCheckedChange={(v) => update('extract_linked_pdfs', v)} />
      </div>

      {/* Linked Office Docs */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">Follow Linked Office Docs</Label>
          <p className="text-[10px] text-gray-500">Process DOCX, XLSX, PPTX linked within documents</p>
        </div>
        <Switch checked={value.extract_linked_office_docs} onCheckedChange={(v) => update('extract_linked_office_docs', v)} />
      </div>

      {/* External Hyperlinks Mode */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">External Hyperlinks</Label>
          <p className="text-[10px] text-gray-500">How to handle links to other websites</p>
        </div>
        <Select value={value.external_hyperlink_mode} onValueChange={(v) => update('external_hyperlink_mode', v)}>
          <SelectTrigger className="w-36 h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ignore">Ignore</SelectItem>
            <SelectItem value="record_only">Record Only</SelectItem>
            <SelectItem value="follow_shallow">Follow (1 level)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Max Items Per Type */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Label className="text-xs font-medium">Max Items Per Type</Label>
          <p className="text-[10px] text-gray-500">Limit how many of each media type are processed</p>
        </div>
        <Input type="number" min={1} value={value.max_items_per_type} onChange={(e) => update('max_items_per_type', parseInt(e.target.value) || 1)} className="w-20 h-7 text-xs" />
      </div>

      {/* Reset to Defaults */}
      <div className="pt-2 border-t">
        <Button variant="ghost" size="sm" className="text-xs h-6 text-gray-500" onClick={onResetToDefaults} disabled={loading}>
          <RotateCcw className="h-3 w-3 mr-1" /> Reset to system defaults
        </Button>
      </div>
    </div>
  );

  if (!collapsible) {
    return content;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Scan Options
      </button>
      {expanded && (
        <div className="px-3 py-3 border-t bg-white">
          {loading ? (
            <p className="text-xs text-gray-400 text-center py-2">Loading defaults...</p>
          ) : (
            content
          )}
        </div>
      )}
    </div>
  );
}
